import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  Toggle,
  TokenGroup,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import {
  GLOBAL_TEMPLATE_MODIFICATION_REQUEST_TICKET_TEMPLATE_LINK,
  GLOBAL_TEMPLATE_RULE_NAME_TO_USER_FRIENDLY_RULE_NAME,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
  VALIDATE_IS_ISENGARD_ACCOUNT,
  VALIDATE_IS_ISENGARD_ACCOUNT_CUSTOM_PATTERN,
  VALIDATE_IS_PRODUCTION_ACCOUNT,
  VALIDATE_IS_PRODUCTION_ACCOUNT_CUSTOM_PATTERN,
} from 'src/commons/constants';
import { getDataAgreement, listDataAgreements } from 'src/api/d3a';
import { DataAgreement } from 'aws-sdk/clients/d3aservice';
import { TemplateResult } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import * as validate from 'src/commons/validationUtils';
import { validateApprovalUrl, validateTemplateUrl } from 'src/commons/validationUtils';
import { getAmazonApprovalURL, isApprovalsServiceAvailable, isD3AServiceAvailable } from 'src/api/config';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';

export const ARW_DATA_AGREEMENT_TYPE = 'DataUsageAgreement';
export const ARW_RULE_TYPE = 'ION_EXPRESSION';
export const ARW_RULE_STATUS_ENABLED = 'ENABLED';

export const ARW_INFO_WIKI_PAGE =
  'https://w.amazon.com/bin/view/AWSDataPlatform/AWSDataLake/Omni/Templates/AccessRequestWorkflows';

export const TEMPLATES_INFO_WIKI_PAGE = 'https://w.amazon.com/bin/view/AWSDataPlatform/AWSDataLake/Omni/Templates';

// possible values for Template Type
export const templateTypeOptions = [
  {
    label: 'Access Management',
    value: TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  },
  {
    label: 'Access Request Workflow',
    value: TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
  },
];

export const getDataUsageAgreementsForOwner = async (ownerId: string): Promise<DataAgreement[]> => {
  try {
    let listAgreementsRes = await listDataAgreements({
      ownerId: ownerId,
      type: ARW_DATA_AGREEMENT_TYPE,
    });
    let agreements = listAgreementsRes.dataAgreements;
    while (listAgreementsRes.nextToken != undefined) {
      listAgreementsRes = await listDataAgreements({
        ownerId: ownerId,
        type: ARW_DATA_AGREEMENT_TYPE,
        nextToken: listAgreementsRes.nextToken,
      });
      agreements.push(...listAgreementsRes.dataAgreements);
    }
    return agreements;
  } catch (err) {
    console.error('Failed to fetch data agreements from D3A with OwnerId:' + ownerId, err);
  }
  return [];
};

export function getApprovalTemplateLink() {
  return (item: TemplateResult) => {
    let amLink = item?.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateLink;
    if (amLink) {
      return (
        <Link href={amLink} external={true}>
          {amLink.includes('/') ? amLink.split('/').pop() : 'Approval template link'}
        </Link>
      );
    }
    if (item?.accessRequestWorkflows) {
      let approvalsLinkList = item.accessRequestWorkflows
        .filter((arw) => !!arw.approvalsWorkflowTemplate)
        .map((arw) => arw.approvalsWorkflowTemplate.approvalTemplateLink);
      return (
        <>
          {approvalsLinkList.map((link, index) => (
            <Link key={index} href={link} external={true}>
              {link.includes('/') ? link.split('/').pop() : 'Approval template link'}
              {index < approvalsLinkList.length - 1 && <br />}
            </Link>
          ))}
        </>
      );
    }
    return 'None';
  };
}

export function getStatusComp(enabled: Boolean) {
  return enabled !== undefined ? (
    <StatusIndicator type={enabled.toString() == 'true' ? 'success' : 'stopped'}>
      {enabled.toString() == 'true' ? 'Enabled' : 'Disabled'}
    </StatusIndicator>
  ) : (
    'None'
  );
}

export function getStatusCompList(enabledList: Boolean[]) {
  return (
    <>
      {enabledList.map((enabled, index) =>
        enabled !== undefined ? (
          <StatusIndicator key={index} type={enabled.toString() == 'true' ? 'success' : 'stopped'}>
            {enabled.toString() == 'true' ? 'Enabled' : 'Disabled'}
            {index < enabledList.length - 1 && <br />}
          </StatusIndicator>
        ) : (
          <> None {index < enabledList.length - 1 && <br />}</>
        ),
      )}
    </>
  );
}

export function getApprovalAutoApprovalEnabled() {
  return (item: TemplateResult) => {
    let amLink = item?.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateLink;
    if (amLink) {
      const autoApproval = item.accessManagementInfo?.approvalsWorkflowTemplate?.autoApproval;
      return getStatusComp(autoApproval);
    }
    if (item?.accessRequestWorkflows) {
      let autoApprovalsList = item.accessRequestWorkflows
        .filter((arw) => !!arw.approvalsWorkflowTemplate)
        .map((arw) => arw.approvalsWorkflowTemplate.autoApproval);
      return getStatusCompList(autoApprovalsList);
    }
    return 'None';
  };
}

export function getApprovalMergeEnabled() {
  return (item: TemplateResult) => {
    let amLink = item?.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateLink;
    if (amLink) {
      const mergeEnabled = item.accessManagementInfo?.approvalsWorkflowTemplate?.allowMergeApprovalWorkflow;
      return getStatusComp(mergeEnabled);
    }
    if (item?.accessRequestWorkflows) {
      let autoMergedList = item.accessRequestWorkflows
        .filter((arw) => !!arw.approvalsWorkflowTemplate)
        .map((arw) => arw.approvalsWorkflowTemplate.allowMergeApprovalWorkflow);
      return getStatusCompList(autoMergedList);
    }
    return 'None';
  };
}

export function getSimTemplateLink() {
  return (item) =>
    item?.accessManagementInfo?.simTemplates?.[0]?.simTemplateLink ? (
      <Link href={item.accessManagementInfo?.simTemplates?.[0]?.simTemplateLink} external={true}>
        {'SIM template link'}
      </Link>
    ) : (
      'None'
    );
}

export function getConditionRules() {
  return (item) => {
    if (item?.accessRequestWorkflows) {
      let conditionRules = item.accessRequestWorkflows
        .filter((arw) => !!arw.conditionRule)
        .map((arw) => arw.conditionRule);
      return (
        <>
          {conditionRules.map((rule, index) =>
            rule !== undefined ? (
              <span key={rule.name}>
                {`${rule.name} (${rule.rule})`}
                {index < conditionRules.length - 1 && <br />}
              </span>
            ) : (
              <> None {index < conditionRules.length - 1 && <br />}</>
            ),
          )}
        </>
      );
    }
    return 'None';
  };
}

export function getDataAgreements() {
  return (item, setArwDataAgreementModalVisible?: any) => {
    if (item?.accessRequestWorkflows) {
      let agreements = item.accessRequestWorkflows.filter((arw) => !!arw.dataAgreement).map((arw) => arw.dataAgreement);
      return (
        <>
          {agreements.map((rule, index) =>
            rule !== undefined ? (
              <SpaceBetween size={'xxs'} direction={'vertical'}>
                <span key={rule.agreementId}>
                  {`${rule.agreementName} (${rule.agreementId})`}
                  {index < agreements.length - 1 && <br />}
                </span>
                <Link onClick={() => !!setArwDataAgreementModalVisible && setArwDataAgreementModalVisible(true)}>
                  Preview agreement
                </Link>
              </SpaceBetween>
            ) : (
              <> None {index < agreements.length - 1 && <br />}</>
            ),
          )}
        </>
      );
    }
    return 'None';
  };
}

export const getDataAgreementOptionsForOwner = async (ownerIds: string[]): Promise<{ label: any; value: any }[]> => {
  const dataAgreements = [];
  for (const ownerId of ownerIds) {
    dataAgreements.push(...(await getDataUsageAgreementsForOwner(ownerId)));
  }
  if (dataAgreements) {
    return dataAgreements.map((dataAgreement: any) => {
      const nameAndDesc = dataAgreement.description
        ? `${dataAgreement.name} (${dataAgreement.description})`
        : dataAgreement.name;
      return {
        label: nameAndDesc,
        description: `Owned by ${dataAgreement.ownerId}`,
        value: dataAgreement,
      };
    });
  }
  return [];
};

export const isAccessManagementType = (template: TemplateResult): boolean => {
  return template && template.templateType == TEMPLATE_TYPE_ACCESS_MANAGEMENT;
};

export const isAccessRequestWorkflowType = (template: TemplateResult): boolean => {
  return template && template.templateType == TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW;
};

export function populateAccessManagementInfo(
  simTemplateName,
  simTemplateLink,
  approvalTemplateLink,
  approvalTemplateTitle,
  autoApproval,
  allowMergeApprovalWorkflow,
) {
  const accessManagementInfo = {};
  if (simTemplateName && simTemplateName.length > 0) {
    accessManagementInfo['simTemplates'] = [
      {
        simTemplateLink: simTemplateLink,
        simTemplateName: simTemplateName,
      },
    ];
  }
  if (approvalTemplateLink && approvalTemplateLink.length > 0) {
    accessManagementInfo['approvalsWorkflowTemplate'] = {
      approvalTemplateLink: approvalTemplateLink,
      approvalTemplateTitle: approvalTemplateTitle,
      autoApproval: autoApproval,
      allowMergeApprovalWorkflow: allowMergeApprovalWorkflow,
    };
  }
  return accessManagementInfo;
}

export function populateAccessRequestWorkflows(
  arwRule,
  arwRuleName,
  arwRuleDesc,
  arwDataAgreementId,
  arwDataAgreementName,
  arwDataAgreementOwnerId,
  approvalTemplateLink,
  approvalTemplateTitle,
  autoApproval,
  allowMergeApprovalWorkflow,
) {
  // Though a list is allowed, use a single value for now.
  const accessRequestWorkflow = {};
  // Set condition rule if provided
  if (arwRule && arwRule.length > 0) {
    accessRequestWorkflow['conditionRule'] = {
      name: arwRuleName,
      rule: arwRule,
      description: arwRuleDesc,
      status: ARW_RULE_STATUS_ENABLED,
      type: ARW_RULE_TYPE,
    };
  }
  // Set data agreement if provided
  if (arwDataAgreementId && arwDataAgreementId.length > 0) {
    accessRequestWorkflow['dataAgreement'] = {
      agreementId: arwDataAgreementId,
      agreementType: ARW_DATA_AGREEMENT_TYPE,
      agreementName: arwDataAgreementName,
      agreementOwnerId: arwDataAgreementOwnerId,
    };
  }
  // Set approval template if provided.
  if (approvalTemplateLink && approvalTemplateLink.length > 0) {
    accessRequestWorkflow['approvalsWorkflowTemplate'] = {
      approvalTemplateLink: approvalTemplateLink,
      approvalTemplateTitle: approvalTemplateTitle,
      autoApproval: autoApproval,
      allowMergeApprovalWorkflow: allowMergeApprovalWorkflow,
    };
  }
  return [accessRequestWorkflow];
}

export function getApprovalTemplateComponent(
  approvalTemplateTitle: any,
  setApprovalTemplateTitle: React.Dispatch<any>,
  approvalTemplateLink: any,
  setApprovalTemplateLink: React.Dispatch<any>,
  autoApproval: any,
  setAutoApproval: React.Dispatch<any>,
  allowMergeApprovalWorkflow: any,
  setAllowMergeApprovalWorkflow: React.Dispatch<any>,
) {
  return (
    <ExpandableSection defaultExpanded headerText='Approval template'>
      <FormField label={'Approval template title '} description='A title of the approval template for use on Omni.'>
        <Input
          onChange={({ detail }) => setApprovalTemplateTitle(detail.value)}
          ariaRequired={true}
          value={approvalTemplateTitle}
          placeholder='My approval template'
          invalid={approvalTemplateTitle != null && validate.isFalsy(approvalTemplateTitle)}
        />
      </FormField>
      <br />
      <FormField
        label={<div>Approval template link</div>}
        description={
          <div>
            A link to create the approval workflow from a template. Click&nbsp;
            <Link external href={getAmazonApprovalURL()}>
              here
            </Link>{' '}
            to create approval template{' '}
          </div>
        }
      >
        <Input
          name='template-details'
          placeholder='https://approvals.amazon.com/Template/Details/1234'
          ariaRequired={true}
          onChange={(event) => setApprovalTemplateLink(event.detail.value.trim())}
          invalid={approvalTemplateLink !== null && validateApprovalUrl(approvalTemplateLink) == false}
          value={approvalTemplateLink}
        />
      </FormField>
      <br />
      <Toggle
        onChange={({ detail }) => setAutoApproval(detail.checked)}
        checked={autoApproval}
        description='Automatically approve or deny requests in Omni based on the approval workflow status.'
      >
        Auto approval
      </Toggle>
      <br />
      <Toggle
        onChange={({ detail }) => setAllowMergeApprovalWorkflow(detail.checked)}
        checked={allowMergeApprovalWorkflow}
        description='Allow merging approval workflows. If enabled, the request will be merged to the
                           existing approval workflow. If disabled, will create a new approval workflow.'
      >
        Allow merge workflows
      </Toggle>
    </ExpandableSection>
  );
}

export function getAccessManagementComponent(
  simTemplateName: any,
  setSimTemplateName: React.Dispatch<any>,
  simTemplateLink: any,
  setSimTemplateLink: React.Dispatch<any>,
  approvalTemplateTitle: any,
  setApprovalTemplateTitle: React.Dispatch<any>,
  approvalTemplateLink: any,
  setApprovalTemplateLink: React.Dispatch<any>,
  autoApproval: any,
  setAutoApproval: React.Dispatch<any>,
  allowMergeApprovalWorkflow: any,
  setAllowMergeApprovalWorkflow: React.Dispatch<any>,
) {
  return (
    <>
      AccessManagement templates allow associating SIM/Taskei tickets or Amazon approval workflows to Omni resources
      like datasets, databases and catalogs. When associated, the consumers will be required to complete these SIM or
      Amazon approvals during access requests.
      <ExpandableSection defaultExpanded headerText='SIM template'>
        <FormField label={<div>SIM template name</div>} description='A name to associate with the template URL.'>
          <Input
            name='template-details'
            placeholder='Name of the SIM template'
            ariaRequired={true}
            onChange={(event) => setSimTemplateName(event.detail.value)}
            invalid={simTemplateName !== null && validate.isFalsy(simTemplateName)}
            value={simTemplateName}
          />
        </FormField>
        <br />
        <FormField label={'SIM template link '} description='A link to fill out a ticket using this template.'>
          <Input
            onChange={({ detail }) => setSimTemplateLink(detail.value.trim())}
            ariaRequired={true}
            value={simTemplateLink}
            placeholder='https://sim.amazon.com/123456789'
            invalid={simTemplateLink != null && validateTemplateUrl(simTemplateLink) == false}
          />
        </FormField>
      </ExpandableSection>
      {isApprovalsServiceAvailable() &&
        getApprovalTemplateComponent(
          approvalTemplateTitle,
          setApprovalTemplateTitle,
          approvalTemplateLink,
          setApprovalTemplateLink,
          autoApproval,
          setAutoApproval,
          allowMergeApprovalWorkflow,
          setAllowMergeApprovalWorkflow,
        )}
    </>
  );
}

export function onDataAgreementChange(
  setArwDataAgreementOwnerId,
  setArwDataAgreementName,
  setArwDataAgreementId,
  arwDataAgreementOptions,
  setArwDataAgreement,
) {
  return ({ detail }) => {
    setArwDataAgreementId(detail.selectedOption.value.id);
    // set other attributes
    let dataAgreement = arwDataAgreementOptions.find((option: any) => option.value == detail.selectedOption.value);
    if (dataAgreement) {
      setArwDataAgreementOwnerId(dataAgreement.value.ownerId);
      setArwDataAgreementName(dataAgreement.value.name);
      setArwDataAgreement(dataAgreement.value);
    }
  };
}

export function getUsageAgreementModal(
  setArwDataAgreementModalVisible,
  arwDataAgreementModalVisible,
  arwDataAgreement,
) {
  // used for preview/display purposes.
  const dummyOmniContext: OmniDUAContext = {
    consumerAccountId: '<Consumer AWS account ID>',
  };
  return (
    <Modal
      onDismiss={() => setArwDataAgreementModalVisible(false)}
      visible={arwDataAgreementModalVisible}
      size={'large'}
      header={arwDataAgreement?.name}
      footer={
        <Box float='right'>
          <Button variant='primary' onClick={() => setArwDataAgreementModalVisible(false)}>
            Close
          </Button>
        </Box>
      }
    >
      <UsageAgreementForm
        schema={JSON.stringify(arwDataAgreement?.jsonSchema)}
        formData={{}}
        setFormData={() => {}}
        errors={[]}
        setErrors={() => {}}
        readOnly={true}
        omniContext={dummyOmniContext}
      />
    </Modal>
  );
}

export function getAccessRequestWorkflowComponent(
  arwRuleName,
  setArwRuleName,
  arwRuleDesc,
  setArwRuleDesc,
  arwRule,
  setArwRule,
  arwDataAgreementOptions,
  arwDataAgreement,
  setArwDataAgreement,
  arwDataAgreementModalVisible,
  setArwDataAgreementModalVisible,
  setArwDataAgreementOwnerId,
  arwDataAgreementId,
  setArwDataAgreementId,
  arwDataAgreementName,
  setArwDataAgreementName,
  approvalTemplateTitle,
  setApprovalTemplateTitle,
  approvalTemplateLink,
  setApprovalTemplateLink,
  autoApproval,
  setAutoApproval,
  allowMergeApprovalWorkflow,
  setAllowMergeApprovalWorkflow,
) {
  function onDataAgreementIdChange() {
    return (event) => {
      const agreementId = event.detail.value;
      setArwDataAgreementId(agreementId);
      if (agreementId.length == 36 && (!arwDataAgreement || arwDataAgreement.id != agreementId)) {
        console.log('Fetching agreement with id:', agreementId);
        getDataAgreement({ id: agreementId }).then(
          (apiResp) => {
            setArwDataAgreement(apiResp.dataAgreements[0]);
            setArwDataAgreementOwnerId(apiResp.dataAgreements[0].ownerId);
            setArwDataAgreementName(apiResp.dataAgreements[0].name);
          },
          (err) => {
            console.error(`Failed to get data agreement with id: ${agreementId}`, err);
            setArwDataAgreement(null);
            setArwDataAgreementOwnerId(null);
            setArwDataAgreementName(null);
          },
        );
      } else if (agreementId.length != 36) {
        setArwDataAgreement(null);
        setArwDataAgreementOwnerId(null);
        setArwDataAgreementName(null);
      }
    };
  }

  return (
    <>
      <Alert statusIconAriaLabel='Info'>
        AccessRequestWorkflow templates allow creating workflows involving Data agreements and Amazon approval workflows
        during access requests. When associated, the consumers will be required to complete these agreements and Amazon
        approvals during access requests. The condition rule can be used to conditionally match certain parameters based
        on the access requestor and only then enable the workflow.
      </Alert>
      {isD3AServiceAvailable() &&
        !!arwDataAgreement &&
        getUsageAgreementModal(setArwDataAgreementModalVisible, arwDataAgreementModalVisible, arwDataAgreement)}
      {isD3AServiceAvailable() && (
        <ExpandableSection defaultExpanded headerText='Condition rule (Optional)'>
          <SpaceBetween size={'m'} direction={'vertical'}>
            <FormField label={'Rule name'} description='Name for the rule.'>
              <Input
                placeholder='Name of the condition rule'
                ariaRequired={true}
                onChange={(event) => setArwRuleName(event.detail.value)}
                invalid={arwRuleName !== null && validate.isFalsy(arwRuleName)}
                value={arwRuleName}
              />
            </FormField>
            <FormField
              label={'Description'}
              description='Description for the rule, explaning when the rule matches some conditions.'
            >
              <Input
                placeholder='Description of the condition rule'
                ariaRequired={true}
                onChange={(event) => setArwRuleDesc(event.detail.value)}
                invalid={arwRuleDesc !== null && validate.isFalsy(arwRuleDesc)}
                value={arwRuleDesc}
              />
            </FormField>
            <FormField
              label={'Rule expression'}
              info={
                <Link href={ARW_INFO_WIKI_PAGE} external={true}>
                  Info
                </Link>
              }
              description={
                'Rule expression that matches some access requests. eg: ' +
                '(is_member_of $dataLakePrincipalAccountPrimaryOwner [{group_type:"posixGroup", group_id: "all-aws-employees"}])'
              }
            >
              <Textarea
                placeholder='(not(is_member_of $dataLakePrincipalAccountPrimaryOwner [{group_type:\"posixGroup\", group_id: \"mp-core\"}]))'
                ariaRequired={true}
                onChange={({ detail }) => setArwRule(detail.value)}
                value={arwRule}
                invalid={arwRule !== null && validate.isFalsy(arwRule)}
              />
            </FormField>
          </SpaceBetween>
        </ExpandableSection>
      )}
      {isD3AServiceAvailable() && (
        <ExpandableSection defaultExpanded headerText='Data usage agreement (Optional)'>
          <FormField
            label={'Data usage agreement selection'}
            description={`Select a data usage agreement which is global or owned by the current group/workspace. `}
          >
            <Select
              selectedOption={arwDataAgreementOptions.find((opt) => opt.value.id == arwDataAgreementId)}
              ariaRequired={false}
              placeholder='Choose a data usage agreement'
              onChange={onDataAgreementChange(
                setArwDataAgreementOwnerId,
                setArwDataAgreementName,
                setArwDataAgreementId,
                arwDataAgreementOptions,
                setArwDataAgreement,
              )}
              empty={'No data usage agreements'}
              options={arwDataAgreementOptions}
              selectedAriaLabel='Selected'
            />
          </FormField>
          <br />
          <FormField
            label={'Data agreement ID'}
            info={<Link onClick={() => setArwDataAgreementModalVisible(!!arwDataAgreement)}>Preview agreement</Link>}
            description='ID for the data agreement. Use the dropdown above to find the agreement for this group or workspace, or provide the ID below.'
          >
            <Input
              placeholder='dua-3184c346a9b74511aa975e4e533edefd'
              ariaRequired={true}
              onChange={onDataAgreementIdChange()}
              invalid={arwDataAgreementId !== null && validate.isFalsy(arwDataAgreementId)}
              value={arwDataAgreementId}
            />
          </FormField>
          <br />
          <FormField label={<div>Data agreement name</div>} description='Name for the data agreement.'>
            <Input
              placeholder='AWS MP data agreement'
              ariaRequired={true}
              onChange={(event) => setArwDataAgreementName(event.detail.value)}
              invalid={arwDataAgreementName !== null && validate.isFalsy(arwDataAgreementName)}
              value={arwDataAgreementName}
            />
          </FormField>
        </ExpandableSection>
      )}
      {isApprovalsServiceAvailable() &&
        getApprovalTemplateComponent(
          approvalTemplateTitle,
          setApprovalTemplateTitle,
          approvalTemplateLink,
          setApprovalTemplateLink,
          autoApproval,
          setAutoApproval,
          allowMergeApprovalWorkflow,
          setAllowMergeApprovalWorkflow,
        )}
    </>
  );
}

export const TemplateDetailsComponent = ({ template, setArwDataAgreementModalVisible }) => (
  <Container header={<Header variant={'h2'}>Template details</Header>}>
    <KeyValuePairs
      columns={isAccessRequestWorkflowType(template) ? 3 : 4}
      items={[
        { label: 'Owner', value: template.ownerId },
        { label: 'Template ID', value: template.templateId },
        { label: 'Template name', value: template.name },
        { label: 'Scope', value: template.scope },
        { label: 'Template type', value: template.templateType },
        { label: 'Template description', value: template.description },
        isAccessManagementType(template)
          ? {
              label: 'SIM template link',
              value: getSimTemplateLink().call(this, template),
            }
          : undefined,
        isAccessRequestWorkflowType(template)
          ? {
              label: 'Condition rule',
              value: getConditionRules().call(this, template),
            }
          : undefined,
        isAccessRequestWorkflowType(template)
          ? {
              label: 'Data agreement',
              value: getDataAgreements().call(this, template, setArwDataAgreementModalVisible),
            }
          : undefined,
        isAccessManagementType(template) || isAccessRequestWorkflowType(template)
          ? {
              label: 'Approval template link',
              value: getApprovalTemplateLink().call(this, template),
            }
          : undefined,
        isAccessManagementType(template) || isAccessRequestWorkflowType(template)
          ? {
              label: 'Auto approval',
              value: getApprovalAutoApprovalEnabled().call(this, template),
            }
          : undefined,
        isAccessManagementType(template) || isAccessRequestWorkflowType(template)
          ? {
              label: 'Auto merge workflows',
              value: getApprovalMergeEnabled().call(this, template),
            }
          : undefined,
      ].filter((item) => item != undefined)}
    />
    {getTemplateRules(template)}
  </Container>
);

const getUserFriendlyRuleName = (ruleName) => {
  if (!ruleName) return ruleName;
  let after =
    ruleName in GLOBAL_TEMPLATE_RULE_NAME_TO_USER_FRIENDLY_RULE_NAME
      ? GLOBAL_TEMPLATE_RULE_NAME_TO_USER_FRIENDLY_RULE_NAME[ruleName]
      : ruleName;
  return after;
};

const getRuleExceptionItems = (baseKey, rules) => {
  // Base rule is disabled. No exceptions should exist.
  if (!rules[baseKey]) return [];

  const items = [];
  Object.entries(rules).forEach(([key, value]) => {
    switch (baseKey) {
      case VALIDATE_IS_ISENGARD_ACCOUNT:
        if (key.startsWith(VALIDATE_IS_ISENGARD_ACCOUNT_CUSTOM_PATTERN) && !value) {
          const accountId = key.split(VALIDATE_IS_ISENGARD_ACCOUNT_CUSTOM_PATTERN)[1];
          items.push({
            label: accountId,
            description: `Account is exempted from this rule`,
            disabled: true,
          });
        }
        break;
      case VALIDATE_IS_PRODUCTION_ACCOUNT:
        if (key.startsWith(VALIDATE_IS_PRODUCTION_ACCOUNT_CUSTOM_PATTERN) && !value) {
          const accountId = key.split(VALIDATE_IS_PRODUCTION_ACCOUNT_CUSTOM_PATTERN)[1];
          items.push({
            label: accountId,
            description: `Account is exempted from this rule`,
            disabled: true,
          });
        }
        break;
    }
  });
  return items;
};

export const getTemplateRules = (template) => {
  if (
    !template ||
    !template.accessManagementInfo ||
    !template.accessManagementInfo.booleanRules ||
    !Object.keys(template.accessManagementInfo.booleanRules).length
  )
    return;

  const rules = template.accessManagementInfo.booleanRules;
  const rulesToDisplay = [VALIDATE_IS_ISENGARD_ACCOUNT, VALIDATE_IS_PRODUCTION_ACCOUNT];
  rulesToDisplay.sort();
  const ruleDivs = [];
  rulesToDisplay.forEach((key) => {
    if (!(key in rules)) return;
    const ruleName = getUserFriendlyRuleName(key);
    const exceptionItems = getRuleExceptionItems(key, rules);
    ruleDivs.push(
      <div>
        <div>
          <Toggle checked={rules[key]} disabled={true}>
            {ruleName}
          </Toggle>
        </div>
        <div>
          <TokenGroup items={exceptionItems} />
        </div>
      </div>,
    );
  });

  const descriptionMsg = `Rules for your ${
    template.resourceId
  } ${template.scope.toLowerCase()}. To modify, submit a request `;
  return (
    <ExpandableSection
      headerText='Rules'
      defaultExpanded={true}
      headerDescription={
        (
          <>
            {descriptionMsg}
            <Link variant='info' href={GLOBAL_TEMPLATE_MODIFICATION_REQUEST_TICKET_TEMPLATE_LINK}>
              here
            </Link>
            .
          </>
        ) as unknown as string // headerDescription excepts a string
      }
    >
      <ColumnLayout columns={4} variant='text-grid'>
        {ruleDivs}
      </ColumnLayout>
    </ExpandableSection>
  );
};
